import {useContext, useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonDesigns} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import {atomTagDesigns, atomTagSizes} from '@s-ui/react-atom-tag'
import Context from '@s-ui/react-context'
import SearchSave, {searchSaveSizes} from '@adv-mt/mt-search-save'
import SearchTags, {searchTagsPlaces, useSearchTags} from '@adv-mt/mt-search-tags'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft'
import IconAngleRight from '@adv-mt/mt-svg-icons/lib/AngleRight'
import {tagChipTypes} from '@adv-mt/mt-tag-chip/src/index.js'

const BASE_CLASS = 'mt-SearchTagsBar'

export default function SearchTagsBar({
  createdAlert,
  dealerSlug,
  listingSectionId,
  pageName,
  place,
  search,
  searchSaveTrackingOrigin,
  showSearchSave = true
}) {
  const {domain} = useContext(Context)
  const searchTags = useSearchTags({pageName, search})
  const scrollContainerRef = useRef(null)
  const stickyContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isSticky, setIsSticky] = useState(false)

  const hasFiltersApplied = Boolean(searchTags.length)

  const handleOnFilter = ({search, lastFilter}) => {
    domain.get('get_url_params_search_use_case').execute({dealerSlug, lastFilter, listingSectionId, pageName, search})
  }

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current
    if (!container) return

    setCanScrollLeft(container.scrollLeft > 0)
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1)
  }

  const scroll = direction => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    const targetScroll =
      direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount

    container.scrollTo({left: targetScroll, behavior: 'smooth'})
  }

  useEffect(() => {
    checkScrollPosition()
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => checkScrollPosition()
    container.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', checkScrollPosition)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkScrollPosition)
    }
  }, [searchTags])

  useEffect(() => {
    const stickyElement = stickyContainerRef.current
    if (!stickyElement) return

    const checkStickyState = () => {
      const rect = stickyElement.getBoundingClientRect()
      const stickyTop = parseInt(getComputedStyle(stickyElement).top) || 0
      setIsSticky(rect.top <= stickyTop)
    }

    checkStickyState()
    window.addEventListener('scroll', checkStickyState, {passive: true})
    window.addEventListener('resize', checkStickyState)

    return () => {
      window.removeEventListener('scroll', checkStickyState)
      window.removeEventListener('resize', checkStickyState)
    }
  }, [hasFiltersApplied])

  const onPrevClick = () => scroll('left')
  const onNextClick = () => scroll('right')

  const baseClassName = cx(BASE_CLASS, {
    [`${BASE_CLASS}--isSticky`]: isSticky
  })

  if (!search || !hasFiltersApplied) return null

  return (
    <div className={baseClassName} ref={stickyContainerRef}>
      {showSearchSave && (
        <div className={`${BASE_CLASS}-createAlert`}>
          <SearchSave
            createdAlert={createdAlert}
            search={search}
            showTooltip={false}
            trackingOrigin={searchSaveTrackingOrigin}
            size={searchSaveSizes.LARGE}
          />
        </div>
      )}

      <div className={`${BASE_CLASS}-tags`} ref={scrollContainerRef}>
        <SearchTags
          dealerSlug={dealerSlug}
          listingSectionId={listingSectionId}
          onRemoveTag={handleOnFilter}
          pageName={pageName}
          search={search}
          place={searchTagsPlaces.ADS_LIST}
          type={tagChipTypes.FILTERS}
          design={atomTagDesigns.OUTLINE}
          size={atomTagSizes.MEDIUM}
          showLabel={false}
        />
      </div>

      <div
        className={cx(`${BASE_CLASS}-arrows`, {
          [`${BASE_CLASS}-arrows--visible`]: canScrollLeft || canScrollRight
        })}
      >
        <AtomButton
          color={atomButtonColors.NEUTRAL}
          design={atomButtonDesigns.FLAT}
          onClick={onPrevClick}
          disabled={!canScrollLeft}
        >
          <IconAngleLeft size={atomIconSizes.small} aria-hidden role="presentation" />
        </AtomButton>
        <AtomButton
          color={atomButtonColors.NEUTRAL}
          design={atomButtonDesigns.FLAT}
          onClick={onNextClick}
          disabled={!canScrollRight}
        >
          <IconAngleRight size={atomIconSizes.small} aria-hidden role="presentation" />
        </AtomButton>
      </div>
    </div>
  )
}

SearchTagsBar.displayName = 'SearchTagsBar'
SearchTagsBar.propTypes = {
  createdAlert: PropTypes.object,
  dealerSlug: PropTypes.string,
  listingSectionId: PropTypes.string,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object.isRequired,
  searchSaveTrackingOrigin: PropTypes.string,
  showSearchSave: PropTypes.bool
}
